import React, { useRef, useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

/**
 * Home hero visualisation for NeuralWorks — hybrid model.
 *
 * Reads left-to-right as a business delivery journey:
 *
 *  [ Business brief ]  →  [ AI-native design & delivery ]  →  [ Live in market ]
 *
 * The centre zone shows two visible review checkpoints (Expert review · Release
 * approval) so viewers see WHERE governance happens. Business-labelled pills flow
 * through the rail. Some clear both reviews and land in-market; some are sent back
 * for a fix and re-enter the queue.
 */

const NEEDS = [
  { id: 'a', label: 'Launch a new digital product', delay: 0.0, fate: 'pass',   outcome: '3× faster time-to-market' },
  { id: 'b', label: 'Modernise a legacy platform',  delay: 2.6, fate: 'reject', outcome: 'Composable, ready to grow' },
  { id: 'c', label: 'Automate customer service',    delay: 5.2, fate: 'pass',   outcome: '38% faster resolution' },
  { id: 'd', label: 'Personalise at scale',         delay: 7.8, fate: 'pass',   outcome: 'Every session, contextual' },
];
const LOOP = 12;

/* Path builder.
 *   Rail Y  = 210
 *   Reject lane Y = 340
 */
function buildPath(fate) {
  if (fate === 'pass') {
    return {
      x:  [120, 120, 380, 380, 620, 620, 900, 900, 1140],
      y:  [210, 210, 210, 210, 210, 210, 210, 210, 210],
      t:  [0,   0.05, 0.14, 0.22, 0.34, 0.42, 0.58, 0.66, 0.82],
      op: [0,   1,    1,    1,    1,    1,    1,    1,    1],
    };
  }
  return {
    x:  [120, 120, 380, 380, 380, 260, 120],
    y:  [210, 210, 210, 210, 340, 380, 300],
    t:  [0,   0.05, 0.14, 0.22, 0.32, 0.46, 0.62],
    op: [0,   1,    1,    1,    1,    0.5,  0],
  };
}

export default function NeuralWorksBusinessViz() {
  const containerRef = useRef(null);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const smx = useSpring(mx, { stiffness: 60, damping: 22 });
  const smy = useSpring(my, { stiffness: 60, damping: 22 });
  const tiltX = useTransform(smx, [0, 1], [-4, 4]);
  const tiltY = useTransform(smy, [0, 1], [-2, 2]);

  const [count, setCount] = useState(247);
  useEffect(() => {
    const t = setInterval(() => setCount((c) => c + 1), 2200);
    return () => clearInterval(t);
  }, []);

  // Only ONE outcome caption is ever rendered — cycled by state so we cannot
  // have overlapping opacity windows regardless of framer-motion timing quirks.
  const PASS_OUTCOMES = NEEDS.filter((n) => n.fate === 'pass');
  const [outcomeIdx, setOutcomeIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setOutcomeIdx((i) => (i + 1) % PASS_OUTCOMES.length);
    }, 3200);
    return () => clearInterval(t);
  }, [PASS_OUTCOMES.length]);
  const currentOutcome = PASS_OUTCOMES[outcomeIdx];

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      mx.set((e.clientX - r.left) / r.width);
      my.set((e.clientY - r.top) / r.height);
    };
    const onLeave = () => { mx.set(0.5); my.set(0.5); };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [mx, my]);

  return (
    <div
      ref={containerRef}
      className="relative w-full rounded-[28px] bg-[#FBFAF6] border border-[#E8E4D9] overflow-hidden flex flex-col"
      style={{ height: 540 }}
      aria-label="How NeuralWorks turns business briefs into outcomes"
    >
      {/* Meta bar */}
      <div className="h-[52px] px-6 flex items-center justify-between border-b border-[#E8E4D9] bg-[#F4F2EC]/60 backdrop-blur-sm flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="inline-block w-2 h-2 rounded-full bg-[#22A94F] pulse-soft" />
          <span className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/60">
            NeuralWorks · how we get the idea to production
          </span>
        </div>
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/50">
          releases live this month · <span className="text-[#178838] font-semibold">{count}</span>
        </div>
      </div>

      {/* Zone headers */}
      <div className="px-8 pt-6 pb-3 grid grid-cols-3 gap-8 flex-shrink-0">
        <div>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/50">Business brief</div>
          <div className="serif text-[19px] leading-tight mt-1.5 text-[#1A1A1A]">The idea enters.</div>
          <div className="text-[12px] text-[#1A1A1A]/60 mt-1">A product, a modernisation, a workflow.</div>
        </div>
        <div>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/50">AI-native design &amp; delivery</div>
          <div className="serif text-[19px] leading-tight mt-1.5 text-[#1A1A1A]">Experts + AI, in review.</div>
          <div className="text-[12px] text-[#1A1A1A]/60 mt-1">Two checkpoints before anything reaches customers.</div>
        </div>
        <div>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#1A1A1A]/50">Live in market</div>
          <div className="serif text-[19px] leading-tight mt-1.5 text-[#1A1A1A]">Working software.</div>
          <div className="text-[12px] text-[#1A1A1A]/60 mt-1">Shipped, measured, safe.</div>
        </div>
      </div>

      {/* SVG stage */}
      <div className="flex-1 relative px-2 md:px-4 pb-2 min-h-0">
        <motion.svg
          viewBox="0 90 1260 340"
          className="w-full h-full"
          preserveAspectRatio="xMidYMid meet"
          style={{ x: tiltX, y: tiltY }}
        >
          <defs>
            <linearGradient id="briefZone" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#F4F2EC" stopOpacity="0.75" />
              <stop offset="100%" stopColor="#F4F2EC" stopOpacity="0.35" />
            </linearGradient>
            <linearGradient id="deliveryZone" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#0F3D22" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#0F3D22" stopOpacity="0.04" />
            </linearGradient>
            <linearGradient id="liveZone" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#22A94F" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#22A94F" stopOpacity="0.08" />
            </linearGradient>
          </defs>

          {/* Zone panels */}
          <rect x="30"   y="120" width="400" height="200" rx="20" fill="url(#briefZone)"    stroke="#1A1A1A" strokeOpacity="0.10" strokeDasharray="6 4" />
          <rect x="450"  y="120" width="500" height="200" rx="20" fill="url(#deliveryZone)" stroke="#0F3D22" strokeOpacity="0.35" />
          <rect x="970"  y="120" width="260" height="200" rx="20" fill="url(#liveZone)"     stroke="#178838" strokeOpacity="0.55" />

          {/* Rail */}
          <line x1="120" y1="210" x2="1140" y2="210" stroke="#1A1A1A" strokeOpacity="0.20" strokeWidth="1.5" />
          {[
            { x: 120,  tone: '#1A1A1A' },
            { x: 380,  tone: '#1A1A1A' },
            { x: 620,  tone: '#5B4FCF' },
            { x: 900,  tone: '#5B4FCF' },
            { x: 1140, tone: '#22A94F' },
          ].map((n, i) => (<circle key={i} cx={n.x} cy="210" r="5" fill={n.tone} />))}

          {/* Brief source orb */}
          <g transform="translate(120 210)">
            <circle r="22" fill="#FBFAF6" stroke="#1A1A1A" strokeWidth="1.4" strokeDasharray="4 3" />
            <text y="4" textAnchor="middle" style={{ fontSize: 10, fill: '#1A1A1A', fontFamily: 'Inter, sans-serif', fontWeight: 700, letterSpacing: 0.5 }}>BRIEF</text>
          </g>
          <text x="120" y="272" textAnchor="middle" style={{ fontSize: 11, fill: '#1A1A1A', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, opacity: 0.85, fontWeight: 600 }}>
            THE ASK
          </text>

          {/* Reviewer 1 */}
          <g transform="translate(620 210)">
            <circle r="30" fill="#FBFAF6" stroke="#5B4FCF" strokeWidth="1.6" />
            <circle cy="-8" r="8" fill="#5B4FCF" />
            <path d="M -12 10 Q 0 20 12 10" stroke="#5B4FCF" strokeWidth="1.8" fill="none" strokeLinecap="round" />
          </g>
          <text x="620" y="272" textAnchor="middle" style={{ fontSize: 11, fill: '#5B4FCF', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, fontWeight: 700 }}>
            EXPERT REVIEW
          </text>
          <text x="620" y="288" textAnchor="middle" style={{ fontSize: 10, fill: '#1A1A1A', fontFamily: 'Inter, sans-serif', opacity: 0.6 }}>
            approves the plan
          </text>

          {/* Reviewer 2 */}
          <g transform="translate(900 210)">
            <circle r="30" fill="#FBFAF6" stroke="#5B4FCF" strokeWidth="1.6" />
            <circle cy="-8" r="8" fill="#5B4FCF" />
            <path d="M -12 10 Q 0 20 12 10" stroke="#5B4FCF" strokeWidth="1.8" fill="none" strokeLinecap="round" />
          </g>
          <text x="900" y="272" textAnchor="middle" style={{ fontSize: 11, fill: '#5B4FCF', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, fontWeight: 700 }}>
            RELEASE APPROVAL
          </text>
          <text x="900" y="288" textAnchor="middle" style={{ fontSize: 10, fill: '#1A1A1A', fontFamily: 'Inter, sans-serif', opacity: 0.6 }}>
            approves the release
          </text>

          {/* Reject arc */}
          <path
            d="M 620 250 Q 400 400 260 388 T 120 300"
            stroke="#B5822A"
            strokeWidth="1.5"
            fill="none"
            strokeDasharray="4 4"
            opacity="0.6"
          />
          <text x="380" y="420" textAnchor="middle" style={{ fontSize: 11, fill: '#B5822A', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, opacity: 0.85 }}>
            sent back to design · re-enters the queue
          </text>

          {/* Live in market endpoint */}
          <g transform="translate(1140 210)">
            <motion.circle
              r="26" fill="#22A94F" fillOpacity="0.18"
              initial={{ r: 24 }}
              animate={{ r: [24, 34, 24] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            />
            <circle r="22" fill="#0F3D22" />
            <text y="4" textAnchor="middle" style={{ fontSize: 11, fill: '#FBFAF6', fontFamily: 'Inter, sans-serif', fontWeight: 700, letterSpacing: 0.8 }}>LIVE</text>
          </g>
          <text x="1140" y="272" textAnchor="middle" style={{ fontSize: 11, fill: '#178838', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, fontWeight: 700 }}>
            IN MARKET
          </text>

          {/* Boundary — clearly labelled BELOW the top of the Live zone so it never
              collides with outcome captions or the zone rectangle at any resolution. */}
          <line x1="960" y1="145" x2="960" y2="300" stroke="#178838" strokeOpacity="0.4" strokeDasharray="3 4" />
          <text x="960" y="315" textAnchor="middle" style={{ fontSize: 9.5, fill: '#178838', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1, opacity: 0.85, fontWeight: 600 }}>
            THE BOUNDARY
          </text>

          {/* Moving pills — business-labelled */}
          {NEEDS.map((it) => {
            const spec = buildPath(it.fate);
            return (
              <motion.g
                key={it.id}
                initial={{ opacity: 0 }}
                animate={{ x: spec.x, y: spec.y, opacity: spec.op }}
                transition={{
                  duration: LOOP,
                  delay: it.delay,
                  times: spec.t,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <rect x={-115} y={-16} width={230} height={32} rx={16} fill="#FBFAF6" stroke={it.fate === 'pass' ? '#22A94F' : '#B5822A'} strokeWidth={1.5} />
                <circle cx={-98} cy={0} r={5} fill={it.fate === 'pass' ? '#22A94F' : '#B5822A'} />
                <text x={-86} y={4} style={{ fontSize: 12, fill: '#1A1A1A', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                  {it.label}
                </text>
              </motion.g>
            );
          })}

          {/* Rejection flash at Expert Review */}
          {NEEDS.filter((i) => i.fate === 'reject').map((it) => (
            <motion.g
              key={`x-${it.id}`}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: [0, 1, 1, 0], scale: [0.6, 1.15, 1, 0.6] }}
              transition={{
                duration: LOOP,
                delay: it.delay,
                times: [0, 0.22, 0.30, 0.36],
                repeat: Infinity,
              }}
            >
              <circle cx={620} cy={168} r={14} fill="#B5822A" fillOpacity="0.22" />
              <line x1={613} y1={161} x2={627} y2={175} stroke="#B5822A" strokeWidth={2.4} strokeLinecap="round" />
              <line x1={627} y1={161} x2={613} y2={175} stroke="#B5822A" strokeWidth={2.4} strokeLinecap="round" />
            </motion.g>
          ))}

          {/* Approval tick at Release Approval */}
          {NEEDS.filter((i) => i.fate === 'pass').map((it) => (
            <motion.g
              key={`t-${it.id}`}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: [0, 1, 1, 0], scale: [0.5, 1.15, 1, 0.5] }}
              transition={{
                duration: LOOP,
                delay: it.delay,
                times: [0, 0.60, 0.68, 0.74],
                repeat: Infinity,
              }}
            >
              <circle cx={900} cy={168} r={14} fill="#22A94F" fillOpacity="0.25" />
              <path d="M 893 168 l 6 6 l 9 -11" stroke="#178838" strokeWidth={2.4} fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </motion.g>
          ))}

          {/* Outcome caption — SINGLE slot driven by React state so only one is
              ever mounted. Framer-motion keys off outcome.id to cross-fade cleanly. */}
          <motion.g
            key={`o-${currentOutcome.id}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <text x={1100} y={170} textAnchor="middle" style={{ fontSize: 12, fill: '#178838', fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
              {currentOutcome.outcome}
            </text>
          </motion.g>
        </motion.svg>
      </div>

      {/* Bottom rule */}
      <div className="h-[48px] border-t border-[#E8E4D9] bg-[#F4F2EC]/60 px-6 flex items-center justify-between text-[11px] mono flex-shrink-0">
        <div className="text-[#1A1A1A]/60">
          <span className="text-[#178838] font-semibold">The promise ·</span>
          &nbsp;ambitious ideas, reliably delivered &mdash; with governance built-in.
        </div>
        <div className="hidden md:flex items-center gap-5 text-[#1A1A1A]/55">
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#1A1A1A]" /> brief</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#5B4FCF]" /> review</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#22A94F]" /> live</span>
        </div>
      </div>
    </div>
  );
}
